// 現在／直近のプレビュー（過去の実行結果とは明確に分離して表示する）。
import { Button } from '@/components/ui/button';
import { ArrowsClockwise, StopCircle, ListMagnifyingGlass, Warning } from '@phosphor-icons/react';
import { derivePreviewState, shortId } from './nasJobState';

const fmtSec = (s) => {
  if (s == null) return '—';
  const n = Math.max(0, Math.floor(s));
  return n >= 60 ? `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}` : `0:${String(n).padStart(2, '0')}`;
};

const CHIPS = [
  ['files_observed', '検出'], ['eligible_files', '対象'], ['new_files', '新規'],
  ['changed_files', '変更あり'], ['unchanged_files', '変更なし'],
  ['unsupported_extensions', '対象外'], ['oversized_files', 'サイズ超過'],
  ['hidden_files', '隠し'], ['guarded_files', '機密除外'], ['failures', 'エラー'],
];

export function NasPreviewProgressCard({ job, pollError, onCancel, onRefresh, onOpenDetails }) {
  if (!job) return null;
  const st = derivePreviewState(job);
  const active = !!job.is_active;
  const pct = job.total_roots ? Math.round(((job.aggregate?.roots_completed || 0) / job.total_roots) * 100) : 0;
  return (
    <div className={`rounded-lg border ${st.border} ${st.bg} px-4 py-3.5`} data-testid="nas-preview-current"
      data-preview-status={job.status} data-preview-state={st.key}>
      <div className="flex flex-wrap items-center gap-2">
        <st.Icon size={16} className={`${st.color} shrink-0 ${st.spin ? 'animate-spin' : ''}`} />
        <p className={`text-[13px] font-bold ${st.color}`} data-testid="nas-preview-status-label">
          {active ? '現在のプレビュー' : '直近のプレビュー'} — {st.labelJa}
        </p>
        <span className="text-[10.5px] text-zinc-500">
          {active ? 'Current preview' : 'Latest preview'} — {st.labelEn}
        </span>
        <span className="text-[10.5px] text-zinc-500 font-mono">ID {shortId(job.job_id)}</span>
        <div className="flex-1" />
        <span className={`text-[11px] font-semibold ${st.color} tabular-nums`}>経過 {fmtSec(job.elapsed_seconds)}</span>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="nas-preview-mode-row"
        data-preview-mode={job.preview_mode || ''}>
        <span className={`text-[10.5px] px-2 py-0.5 rounded-full border font-semibold ${
          job.preview_mode === 'full_validation'
            ? 'border-teal-300 bg-teal-50 text-teal-800'
            : 'border-zinc-300 bg-white text-zinc-700'}`}>
          {job.preview_mode_label_ja || (job.preview_mode === 'full_validation' ? '完全検証プレビュー' : '簡易見積り')}
          {' / '}{job.preview_mode_label_en || job.preview_mode}
        </span>
        {job.pagination_used && (
          <span className="text-[10.5px] text-zinc-600">
            ページ {job.aggregate?.pages ?? 0} · 整合性 {job.consistency === 'best_effort_stable'
              ? '安定（最善努力）' : job.consistency === 'changed_during_enumeration'
                ? '列挙中に変化' : '未検証'}
          </span>
        )}
        <span className={`text-[10.5px] px-2 py-0.5 rounded-full border ${
          job.authorises_ingestion
            ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
            : 'border-zinc-300 bg-white text-zinc-600'}`}
          data-testid="nas-preview-authorises">
          {job.authorises_ingestion
            ? '取り込みを許可できます / Can authorise ingestion'
            : '取り込みは許可されません / Does not authorise ingestion'}
        </span>
      </div>

      <div className="mt-2 h-2 rounded-full bg-white/70 border border-zinc-200 overflow-hidden">
        <div className="h-full bg-teal-700 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[11px] text-zinc-600 mt-1" data-testid="nas-preview-progress">
        クロール対象パス {job.aggregate?.roots_completed || 0} / {job.total_roots || 0} 完了（{pct}%）
        {job.current_root && active && <span className="font-mono ml-1">現在: {job.current_root}</span>}
      </p>

      <div className="mt-2 flex flex-wrap gap-1.5" data-testid="nas-preview-totals">
        {CHIPS.map(([k, label]) => (
          <span key={k} className={`px-2 py-1 rounded border bg-white text-[11px] ${
            k === 'failures' && (job.aggregate?.[k] || 0) > 0
              ? 'border-rose-300 text-rose-800' : 'border-zinc-300 text-zinc-800'}`}>
            {label} <b className="tabular-nums">{job.aggregate?.[k] ?? 0}</b>
          </span>
        ))}
      </div>

      {job.truncated && (
        <p className="mt-2 text-[11.5px] font-semibold text-amber-800" data-testid="nas-preview-truncated">
          <Warning size={13} weight="fill" className="inline mr-1" />
          {job.truncation_message_ja}（<span className="font-mono">{job.truncation_reason}</span>）
          <span className="block font-normal opacity-80">{job.truncation_message_en}</span>
        </p>
      )}
      {job.typed_error && (
        <p className="mt-1.5 text-[11.5px] text-rose-800" data-testid="nas-preview-typed-error">
          {job.typed_error_message_ja}
          <span className="ml-1 font-mono opacity-80">({job.typed_error})</span>
        </p>
      )}
      {pollError && (
        <p className="mt-1.5 text-[11.5px] text-amber-800" data-testid="nas-preview-poll-error">
          状態の取得に失敗しました（プレビューは継続しています）: {pollError}
          <span className="block opacity-80">Status request failed; the preview itself keeps running.</span>
        </p>
      )}

      <div className="mt-2.5 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={(e) => onOpenDetails?.(e)}
          className="h-8 text-[12px]" data-testid="nas-preview-details-btn">
          <ListMagnifyingGlass size={13} className="mr-1" /> 明細を見る / View details
        </Button>
        {active && (
          <>
            <Button size="sm" variant="outline" onClick={onRefresh}
              className="h-8 text-[12px]" data-testid="nas-preview-retry-status-btn">
              <ArrowsClockwise size={13} className="mr-1" /> 状態を再取得 / Retry status
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}
              className="h-8 text-[12px] border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid="nas-preview-cancel-btn">
              <StopCircle size={13} className="mr-1" /> プレビューを中止 / Cancel
            </Button>
          </>
        )}
      </div>
      <p className="mt-1.5 text-[10.5px] text-zinc-500">
        プレビューはインデックスを作成しません / A preview never indexes anything.
      </p>
    </div>
  );
}

export default NasPreviewProgressCard;
